/**
 * CertBadge — small gold seal/checkmark next to a coach's name signaling
 * BAM Certification state.
 *
 * Two states:
 *   in_progress → outline circle with a half-fill, indicates "working on it"
 *   certified   → filled gold seal with a checkmark
 *
 * Driven by `profiles.cert_status`. The trigger sync_cert_status_from_exam
 * keeps this fresh from exam_submissions; ProfileSetup + the cert flow
 * also nudge it on key milestones.
 *
 * Suppressed by BamCoachBadge — internal staff don't show as "certified"
 * because that's assumed. The UserNameWithBadges wrapper handles the
 * priority logic.
 *
 * Usage:
 *   <CertBadge status={profile.cert_status} size="sm" />
 *   <CertBadge status="certified" size="md" tooltip />
 */
import React, { useState } from "react";
import { GOLD } from "../constants/theme";

export function CertBadge({ status, size = "sm", tooltip = true, style }) {
  const [hover, setHover] = useState(false);

  if (status !== "in_progress" && status !== "certified") return null;

  const px = size === "md" ? 18 : 13;
  const isCertified = status === "certified";

  const label = isCertified ? "BAM Certified" : "BAM Cert In Progress";
  const helpText = isCertified
    ? "This coach has earned BAM Certification."
    : "This coach is working through BAM Certification.";

  return (
    <span
      title={tooltip ? helpText : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: px,
        height: px,
        borderRadius: "50%",
        background: isCertified ? GOLD : "transparent",
        border: `1.5px solid ${GOLD}`,
        flexShrink: 0,
        position: "relative",
        cursor: tooltip ? "help" : "default",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {isCertified ? (
        // Filled seal + checkmark
        <svg width={px - 6} height={px - 6} viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M2.5 6.2 5 8.5 9.5 3.5"
            stroke="#111"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      ) : (
        // In progress — half fill (clock-style 12→6 sweep)
        <svg width={px - 4} height={px - 4} viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M6 1 A 5 5 0 0 1 6 11 Z"
            fill={GOLD}
            opacity={0.9}
          />
        </svg>
      )}

      {tooltip && hover && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#111",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "4px 8px",
            borderRadius: 5,
            whiteSpace: "nowrap",
            zIndex: 9999,
            letterSpacing: 0.2,
            fontFamily: "'DM Sans',sans-serif",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            pointerEvents: "none",
          }}
        >
          {label}
        </span>
      )}
    </span>
  );
}

export default CertBadge;
